import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  editModeForParentStartedAction,
  fetchProcessingDuration,
} from '../../redux/modules/annotation/actions';
import { cancelEditMode, startEditMode } from '../../redux/modules/ui/actions';
import { Annotation, AnnotationSideloads } from '../../types/annotation';
import { Organization } from '../../types/organization';
import {
  useCancelEditMode,
  useConfirmEditMode,
  useStartEditMode,
} from '../document-edit-core';

type UseEditModeParams = {
  annotation: Annotation | undefined;
  annotationSideloads: AnnotationSideloads;
  organization: Organization | undefined;
  /** See `useCancelEditMode` — resolved lazily during unmount. */
  getTargetAnnotationId: () => number;
};

export const useEditMode = ({
  annotation,
  annotationSideloads,
  organization,
  getTargetAnnotationId,
}: UseEditModeParams) => {
  const dispatch = useDispatch();

  const { data: config } = useStartEditMode({
    annotation,
    annotationSideloads,
    organization,
  });
  const { mutate: cancelStartedAnnotations } = useCancelEditMode({
    getTargetAnnotationId,
  });

  const confirmHasSucceededRef = useRef<boolean>(false);

  const { mutate: confirm, isLoading: confirmInProgress } = useConfirmEditMode({
    currentQueue: annotationSideloads.queue,
    onSuccess: () => {
      confirmHasSucceededRef.current = true;
    },
  });

  useEffect(() => {
    return () => {
      // If confirm was successful, and we are leaving the edit page,
      // we shouldn't cancel any annotation (they were cancelled by the confirm request)
      if (config && !confirmHasSucceededRef.current) {
        cancelStartedAnnotations(config);
      }
    };
  }, [cancelStartedAnnotations, config]);

  // Integration with legacy Redux state
  useEffect(() => {
    dispatch(startEditMode());

    return () => {
      dispatch(cancelEditMode(annotation?.id));
    };
  }, [dispatch, annotation?.id]);

  useEffect(() => {
    if (config?.mode.type === 'parent-annotation') {
      dispatch(editModeForParentStartedAction(config.annotationUrl));
      dispatch(fetchProcessingDuration(config.annotationUrl));
    }
  }, [config?.annotationUrl, config?.mode.type, dispatch]);

  return {
    config,
    confirm,
    confirmInProgress,
  };
};
